import { LayoutSectionKey, LayoutSectionParentKey } from "@sp-crm/core";
import { ContentFull, ContentHeader } from "components/layout";
import { CommunityCardContactModeSelect } from "components/shared/community-card-contact-mode-select";
import { DefaultStateSelect } from "components/shared/default-state-select";
import {
    AdvancedSearchEntityType,
    useGetEntitiesQuery,
    useGetLayoutSectionsQuery,
} from "generated/graphql";
import React from "react";
import { useBridgeQuestions } from "store/selectors/bridge";
import { useTenantSettings } from "store/selectors/settings";
import { stableQueryOptions } from "util/requests";
import {
    LayoutItemExperience,
    LayoutSectionItemSettings,
} from "../layout-section-item-settings";
import { LayoutSettings } from "../layout-settings";
import { AgencyTogglePref } from "./agency-settings-toggle";
import { EntitySettings } from "./customize/entity-settings";

export const AgencySettingsCommunities: React.FC<unknown> = () => {
    const { settings, putSetting } = useTenantSettings();
    const questions = useBridgeQuestions(AdvancedSearchEntityType.Community);
    const layoutSections = useGetLayoutSectionsQuery(
        { parentKey: LayoutSectionParentKey.CommunityCard },
        stableQueryOptions(),
    );
    const entityTypes = useGetEntitiesQuery({}, stableQueryOptions());

    const communityCardSectionId =
        layoutSections.data?.getLayoutSections?.layoutSections?.find(
            s => s.sectionKey === LayoutSectionKey.CommunityCardKeyAttributes,
        )?.id;

    return (
        <div className="space-y-4 lg:space-y-8">
            <ContentFull>
                <ContentHeader>Community profile</ContentHeader>
                <div className="space-y-4">
                    <AgencyTogglePref
                        field="showCommunityBaseAndHighPrice"
                        label='Show "Monthly base price" and "Price (high end)"'
                    />

                    <AgencyTogglePref
                        field="showCommunitySecondPersonFee"
                        label='Show "Second Person Fee"'
                    />

                    <AgencyTogglePref
                        field="showCommunityPetDeposit"
                        label='Show "Pet Deposit"'
                    />

                    <AgencyTogglePref
                        field="showCommunityFee"
                        label='Show "Community Fee / Entrance Fee"'
                    />
                </div>
            </ContentFull>
            <ContentFull>
                <ContentHeader>Community search</ContentHeader>
                <div className="space-y-4">
                    <AgencyTogglePref
                        field="enableBulkCommunityOptions"
                        label={`Enable bulk add in "Find Ideal Communities"`}
                        description="Allow users to add multiple communities at once when searching for communities in the client profile."
                    />
                    <DefaultStateSelect
                        value={settings.defaultState}
                        onChange={value => putSetting("defaultState", value)}
                    />
                    <CommunityCardContactModeSelect
                        value={settings.communityCardContactMode}
                        onChange={value => putSetting("communityCardContactMode", value)}
                    />
                    {communityCardSectionId ? (
                        <LayoutSectionItemSettings
                            entityMetadataName="Community"
                            includeStandardFields={true}
                            questions={questions}
                            questionFilter={_ => true}
                            questionTitle={q => q.title}
                            categoryFilter={d => d.communityEnabled}
                            categoryTitle={d => d.communityHeading}
                            layoutSectionId={communityCardSectionId}
                            label="Additional fields to show on Community Search results:"
                            mode="alwaysEditing"
                            relations={[]}
                            itemExperience={LayoutItemExperience.communityCard}
                        />
                    ) : null}
                </div>
            </ContentFull>
            <ContentFull>
                <ContentHeader>Community comparison</ContentHeader>
                <div className="space-y-4">
                    <LayoutSettings
                        entityMetadataName="Community"
                        includeStandardFields={true}
                        questions={questions}
                        sectionParentKey={LayoutSectionParentKey.CommunityComparisonTable}
                        label="Configure the layout and visibility for the community comparison pages below"
                        allowEditOrder={true}
                        allowEditVisibility={true}
                        mode="toggleEdit"
                        showContainerTitle={false}
                        allowCreateSections={false}
                        allowEditIcon="never"
                        allowEditTitle="never"
                        itemExperience={LayoutItemExperience.communityComparison}
                    />
                </div>
            </ContentFull>
            {entityTypes.data?.getEntities?.length > 0 ? (
                <ContentFull>
                    <EntitySettings
                        entityName="Community"
                        entities={entityTypes.data.getEntities}
                    />
                </ContentFull>
            ) : null}
        </div>
    );
};
